import React, { useState, useEffect } from 'react';
import { X, Plus, Search, User, Building, Mail, Phone, Edit2, Trash2 } from 'lucide-react';
import { workflowService } from '../services/workflowService';
import { useToast } from '../../../components/ui/Toast';

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: string;
  created_at: string;
}

interface Contact {
  id: number;
  name: string;
  email: string;
  company: string;
  phone?: string;
  created_at: string;
}

interface Target {
  id: string;
  name: string;
  domain?: string;
  description?: string;
  industry?: string;
  companySize?: string;
  status: string;
  priority: string;
  primaryContactEmail?: string;
  primaryContactName?: string;
  contactCount?: number;
  campaignCount?: number;
}

interface WorkflowExecutionDialogProps {
  workflowId: string;
  workflowName: string;
  onClose: () => void;
  onExecute: (result: any) => void;
}

export const WorkflowExecutionDialog: React.FC<WorkflowExecutionDialogProps> = ({
  workflowId,
  workflowName,
  onClose,
  onExecute
}) => {
  const { addToast } = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [selectedContact, setContact] = useState<Contact | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<Target | null>(null);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [isCreatingTarget, setIsCreatingTarget] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  // Edit states
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isEditingCampaign, setIsEditingCampaign] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  
  // Delete states
  const [deletingCampaign, setDeletingCampaign] = useState<Campaign | null>(null);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);
  const [showDeleteCampaignConfirm, setShowDeleteCampaignConfirm] = useState(false);
  const [showDeleteContactConfirm, setShowDeleteContactConfirm] = useState(false);

  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    description: ''
  });

  // New contact form
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    company: ''
  });

  // New target form
  const [newTarget, setNewTarget] = useState({
    name: '',
    domain: '',
    description: '',
    industry: '',
    companySize: '',
    primaryContactEmail: '',
    primaryContactName: ''
  });
  
  // Edit forms
  const [editCampaignForm, setEditCampaignForm] = useState({
    name: '',
    subject: '',
    description: ''
  });

  const [editContactForm, setEditContactForm] = useState({
    name: '',
    email: '',
    company: ''
  });

  const [editTargetForm, setEditTargetForm] = useState({
    name: '',
    domain: '',
    description: '',
    industry: '',
    companySize: '',
    primaryContactEmail: '',
    primaryContactName: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, contactsRes, targetsRes] = await Promise.all([
        fetch('/api/campaigns', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/contacts', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/targets', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => null)
      ]);

      const campaignsData = await campaignsRes.json();
      const contactsData = await contactsRes.json();
      const targetsData = targetsRes ? await targetsRes.json() : { targets: [] };

      if (campaignsData.success) {
        setCampaigns(campaignsData.data || []);
      }
      if (contactsData.success) {
        setContacts(contactsData.data || []);
      }
      if (targetsData.targets) {
        setTargets(targetsData.targets || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      addToast({
        title: 'Error',
        message: 'Failed to load campaigns, contacts, and targets',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) {
      addToast({
        title: 'Validation Error',
        message: 'Campaign name is required',
        type: 'error'
      });
      return;
    }

    try {
      const response = await fetch('/api/campaigns', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newCampaign)
      });

      const data = await response.json();
      if (data.success) {
        setCampaigns([data.data, ...campaigns]);
        setSelectedCampaign(data.data);
        setIsCreatingCampaign(false);
        setNewCampaign({ name: '', subject: '', description: '' });
        addToast({
          title: 'Success',
          message: 'Campaign created successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      addToast({
        title: 'Error',
        message: 'Failed to create campaign',
        type: 'error'
      });
    }
  };

  const handleCreateContact = async () => {
    // Validate required fields
    if (!newContact.name.trim()) {
      addToast({
        title: 'Validation Error',
        message: 'Contact name is required',
        type: 'error'
      });
      return;
    }

    if (!newContact.email.trim()) {
      addToast({
        title: 'Validation Error',
        message: 'Contact email is required',
        type: 'error'
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newContact.email.trim())) {
      addToast({
        title: 'Validation Error',
        message: 'Please enter a valid email address',
        type: 'error'
      });
      return;
    }

    try {
      const response = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newContact.name.trim(),
          email: newContact.email.trim(),
          company: newContact.company.trim() || null
        })
      });

      const data = await response.json();
      if (data.success) {
        setContacts([data.data, ...contacts]);
        setContact(data.data);
        setIsCreatingContact(false);
        setNewContact({ name: '', email: '', company: '' });
        addToast({
          title: 'Success',
          message: 'Contact created successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to create contact');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      addToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create contact',
        type: 'error'
      });
    }
  };

  const handleCreateTarget = async () => {
    if (!newTarget.name.trim()) {
      addToast({
        title: 'Validation Error',
        message: 'Target name is required',
        type: 'error'
      });
      return;
    }

    try {
      const response = await fetch('/api/targets', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newTarget)
      });

      const data = await response.json();
      if (data.target) {
        setTargets([data.target, ...targets]);
        setSelectedTarget(data.target);
        setIsCreatingTarget(false);
        setNewTarget({ 
          name: '', 
          domain: '', 
          description: '', 
          industry: '', 
          companySize: '', 
          primaryContactEmail: '', 
          primaryContactName: '' 
        });
        addToast({
          title: 'Success',
          message: 'Target created successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to create target');
      }
    } catch (error) {
      console.error('Error creating target:', error);
      addToast({
        title: 'Error',
        message: 'Failed to create target',
        type: 'error'
      });
    }
  };

  // Edit campaign functions
  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setEditCampaignForm({
      name: campaign.name,
      subject: campaign.subject,
      description: ''
    });
    setIsEditingCampaign(true);
  };

  const handleUpdateCampaign = async () => {
    if (!editingCampaign || !editCampaignForm.name.trim()) {
      addToast({
        title: 'Validation Error',
        message: 'Campaign name is required',
        type: 'error'
      });
      return;
    }

    try {
      const response = await fetch(`/api/campaigns/${editingCampaign.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editCampaignForm)
      });

      const data = await response.json();
      if (data.success) {
        setCampaigns(campaigns.map(c => c.id === editingCampaign.id ? data.data : c));
        if (selectedCampaign?.id === editingCampaign.id) {
          setSelectedCampaign(data.data);
        }
        setIsEditingCampaign(false);
        setEditingCampaign(null);
        addToast({
          title: 'Success',
          message: 'Campaign updated successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to update campaign');
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      addToast({
        title: 'Error',
        message: 'Failed to update campaign',
        type: 'error'
      });
    }
  };

  // Delete campaign functions
  const handleDeleteCampaign = (campaign: Campaign) => {
    setDeletingCampaign(campaign);
    setShowDeleteCampaignConfirm(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!deletingCampaign) return;

    try {
      const response = await fetch(`/api/campaigns/${deletingCampaign.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      if (data.success) {
        setCampaigns(campaigns.filter(c => c.id !== deletingCampaign.id));
        if (selectedCampaign?.id === deletingCampaign.id) {
          setSelectedCampaign(null);
        }
        setShowDeleteCampaignConfirm(false);
        setDeletingCampaign(null);
        addToast({
          title: 'Success',
          message: 'Campaign deleted successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      addToast({
        title: 'Error',
        message: 'Failed to delete campaign',
        type: 'error'
      });
    }
  };

  // Edit contact functions
  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setEditContactForm({
      name: contact.name,
      email: contact.email,
      company: contact.company
    });
    setIsEditingContact(true);
  };

  const handleUpdateContact = async () => {
    if (!editingContact || !editContactForm.name.trim()) {
      addToast({
        title: 'Validation Error',
        message: 'Contact name is required',
        type: 'error'
      });
      return;
    }

    try {
      const response = await fetch(`/api/contacts/${editingContact.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editContactForm)
      });

      const data = await response.json();
      if (data.success) {
        setContacts(contacts.map(c => c.id === editingContact.id ? data.data : c));
        if (selectedContact?.id === editingContact.id) {
          setContact(data.data);
        }
        setIsEditingContact(false);
        setEditingContact(null);
        addToast({
          title: 'Success',
          message: 'Contact updated successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to update contact');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      addToast({
        title: 'Error',
        message: 'Failed to update contact',
        type: 'error'
      });
    }
  };

  // Delete contact functions
  const handleDeleteContact = (contact: Contact) => {
    setDeletingContact(contact);
    setShowDeleteContactConfirm(true);
  };

  const confirmDeleteContact = async () => {
    if (!deletingContact) return;

    try {
      const response = await fetch(`/api/contacts/${deletingContact.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      if (data.success) {
        setContacts(contacts.filter(c => c.id !== deletingContact.id));
        if (selectedContact?.id === deletingContact.id) {
          setContact(null);
        }
        setShowDeleteContactConfirm(false);
        setDeletingContact(null);
        addToast({
          title: 'Success',
          message: 'Contact deleted successfully',
          type: 'success'
        });
      } else {
        throw new Error(data.error || 'Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      addToast({
        title: 'Error',
        message: 'Failed to delete contact',
        type: 'error'
      });
    }
  };

  const handleExecute = async () => {
    if (!selectedCampaign) {
      addToast({
        title: 'Validation Error',
        message: 'Please select or create a campaign',
        type: 'error'
      });
      return;
    }

    if (!selectedContact) {
      addToast({
        title: 'Validation Error',
        message: 'Please select or create a contact',
        type: 'error'
      });
      return;
    }

    // Target is optional but recommended for better threading
    if (!selectedTarget) {
      console.warn('No target selected - email threading will use fallback method');
    }

    try {
      setExecuting(true);
      
      // Generate a proper UUID for the conversation
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      const executionData: any = {
        campaignId: selectedCampaign.id,
        contactEmail: selectedContact.email,
        contactName: selectedContact.name,
        contactCompany: selectedContact.company,
        conversationId: generateUUID(),
        testMode: true
      };

      // Add target information if selected
      if (selectedTarget) {
        executionData.targetId = selectedTarget.id;
        executionData.targetName = selectedTarget.name;
        executionData.targetDomain = selectedTarget.domain;
      }

      const result = await workflowService.executeWorkflow(workflowId, executionData);

      addToast({
        title: 'Workflow Executed',
        message: `Workflow "${workflowName}" executed successfully${selectedTarget ? ` for target "${selectedTarget.name}"` : ''}`,
        type: 'success'
      });

      onExecute(result);
      onClose();
    } catch (error) {
      console.error('Error executing workflow:', error);
      addToast({
        title: 'Execution Error',
        message: 'Failed to execute workflow',
        type: 'error'
      });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Execute Workflow</h2>
          <button
            onClick={onClose}
            className="p-2 text-neutral-400 hover:text-white rounded-lg hover:bg-neutral-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-neutral-300 mb-2">Workflow: <span className="text-white font-medium">{workflowName}</span></p>
          <p className="text-sm text-neutral-400">Configure campaign and contact settings for this execution</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Campaign Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-white">Campaign</label>
                <button
                  onClick={() => setIsCreatingCampaign(true)}
                  className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create New
                </button>
              </div>

              {isCreatingCampaign ? (
                <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Campaign Name *</label>
                    <input
                      type="text"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                      className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Enter campaign name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Subject</label>
                    <input
                      type="text"
                      value={newCampaign.subject}
                      onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                      className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Enter campaign subject"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateCampaign}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Create Campaign
                    </button>
                    <button
                      onClick={() => setIsCreatingCampaign(false)}
                      className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      onClick={() => setSelectedCampaign(campaign)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedCampaign?.id === campaign.id
                          ? 'bg-blue-600/20 border-blue-500'
                          : 'bg-neutral-800/50 border-neutral-700 hover:border-neutral-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{campaign.name}</h4>
                          <p className="text-sm text-neutral-400">{campaign.subject}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            campaign.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                            campaign.status === 'sent' ? 'bg-green-500/20 text-green-400' :
                            'bg-neutral-500/20 text-neutral-400'
                          }`}>
                            {campaign.status}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCampaign(campaign);
                            }}
                            className="p-1 text-neutral-400 hover:text-blue-400 transition-colors"
                            title="Edit campaign"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCampaign(campaign);
                            }}
                            className="p-1 text-neutral-400 hover:text-red-400 transition-colors"
                            title="Delete campaign"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-white">Contact</label>
                <button
                  onClick={() => setIsCreatingContact(true)}
                  className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create New
                </button>
              </div>

              {isCreatingContact ? (
                <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Contact Name *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Enter contact name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Email *</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Company</label>
                    <input
                      type="text"
                      value={newContact.company}
                      onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                      className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateContact}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Create Contact
                    </button>
                    <button
                      onClick={() => setIsCreatingContact(false)}
                      className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => setContact(contact)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedContact?.id === contact.id
                          ? 'bg-blue-600/20 border-blue-500'
                          : 'bg-neutral-800/50 border-neutral-700 hover:border-neutral-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-neutral-700 rounded-lg">
                          <User className="w-4 h-4 text-neutral-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{contact.name}</h4>
                          <div className="flex items-center gap-4 text-sm text-neutral-400">
                            {contact.email && (
                              <div className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </div>
                            )}
                            {contact.company && (
                              <div className="flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {contact.company}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditContact(contact);
                            }}
                            className="p-1 text-neutral-400 hover:text-blue-400 transition-colors"
                            title="Edit contact"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteContact(contact);
                            }}
                            className="p-1 text-neutral-400 hover:text-red-400 transition-colors"
                            title="Delete contact"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Target Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-white">Target Company</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-400">(Optional - improves threading)</span>
                  <button
                    onClick={() => setIsCreatingTarget(true)}
                    className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create New
                  </button>
                </div>
              </div>

              {isCreatingTarget ? (
                <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-neutral-300 mb-1">Company Name *</label>
                      <input
                        type="text"
                        value={newTarget.name}
                        onChange={(e) => setNewTarget({ ...newTarget, name: e.target.value })}
                        className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="Acme Corp"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-300 mb-1">Domain</label>
                      <input
                        type="text"
                        value={newTarget.domain}
                        onChange={(e) => setNewTarget({ ...newTarget, domain: e.target.value })}
                        className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="acme.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Description</label>
                    <textarea
                      value={newTarget.description}
                      onChange={(e) => setNewTarget({ ...newTarget, description: e.target.value })}
                      className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none resize-none"
                      rows={2}
                      placeholder="Brief description of the target company..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-neutral-300 mb-1">Industry</label>
                      <input
                        type="text"
                        value={newTarget.industry}
                        onChange={(e) => setNewTarget({ ...newTarget, industry: e.target.value })}
                        className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="Technology"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-300 mb-1">Company Size</label>
                      <select
                        value={newTarget.companySize}
                        onChange={(e) => setNewTarget({ ...newTarget, companySize: e.target.value })}
                        className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select size</option>
                        <option value="startup">Startup (1-10)</option>
                        <option value="small">Small (11-50)</option>
                        <option value="medium">Medium (51-250)</option>
                        <option value="large">Large (251-1000)</option>
                        <option value="enterprise">Enterprise (1000+)</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateTarget}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Create Target
                    </button>
                    <button
                      onClick={() => setIsCreatingTarget(false)}
                      className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {targets.length === 0 ? (
                    <div className="p-4 border border-dashed border-neutral-600 rounded-lg text-center">
                      <Building className="w-8 h-8 text-neutral-500 mx-auto mb-2" />
                      <p className="text-neutral-400 text-sm">
                        No targets available. Create one for better email threading.
                      </p>
                    </div>
                  ) : (
                    targets.map((target) => (
                      <div
                        key={target.id}
                        onClick={() => setSelectedTarget(target)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedTarget?.id === target.id
                            ? 'bg-purple-600/20 border-purple-500'
                            : 'bg-neutral-800/50 border-neutral-700 hover:border-neutral-600'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-neutral-700 rounded-lg">
                            <Building className="w-4 h-4 text-neutral-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-white font-medium">{target.name}</h4>
                              {target.domain && (
                                <span className="text-xs text-neutral-400 bg-neutral-700 px-2 py-1 rounded">
                                  {target.domain}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-neutral-400 mt-1">
                              {target.industry && (
                                <span>{target.industry}</span>
                              )}
                              {target.companySize && (
                                <span className="capitalize">{target.companySize}</span>
                              )}
                              {target.contactCount !== undefined && (
                                <span>{target.contactCount} contacts</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs px-2 py-1 rounded ${
                              target.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                              target.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {target.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Execute Button */}
            <div className="flex gap-3 pt-4 border-t border-neutral-800">
              <button
                onClick={handleExecute}
                disabled={executing || !selectedCampaign || !selectedContact}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-700 disabled:text-neutral-400 text-white px-6 py-3 rounded-lg transition-colors"
              >
                {executing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Executing...
                  </>
                ) : (
                  'Execute Workflow'
                )}
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Campaign Modal */}
        {isEditingCampaign && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-white mb-4">Edit Campaign</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-neutral-300 mb-1">Campaign Name *</label>
                  <input
                    type="text"
                    value={editCampaignForm.name}
                    onChange={(e) => setEditCampaignForm({ ...editCampaignForm, name: e.target.value })}
                    className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Enter campaign name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-300 mb-1">Subject</label>
                  <input
                    type="text"
                    value={editCampaignForm.subject}
                    onChange={(e) => setEditCampaignForm({ ...editCampaignForm, subject: e.target.value })}
                    className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Enter campaign subject"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleUpdateCampaign}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Update Campaign
                </button>
                <button
                  onClick={() => setIsEditingCampaign(false)}
                  className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Contact Modal */}
        {isEditingContact && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-white mb-4">Edit Contact</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-neutral-300 mb-1">Contact Name *</label>
                  <input
                    type="text"
                    value={editContactForm.name}
                    onChange={(e) => setEditContactForm({ ...editContactForm, name: e.target.value })}
                    className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Enter contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-300 mb-1">Email *</label>
                  <input
                    type="email"
                    value={editContactForm.email}
                    onChange={(e) => setEditContactForm({ ...editContactForm, email: e.target.value })}
                    className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-300 mb-1">Company</label>
                  <input
                    type="text"
                    value={editContactForm.company}
                    onChange={(e) => setEditContactForm({ ...editContactForm, company: e.target.value })}
                    className="w-full bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Enter company name"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleUpdateContact}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Update Contact
                </button>
                <button
                  onClick={() => setIsEditingContact(false)}
                  className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Campaign Confirmation */}
        {showDeleteCampaignConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-white mb-4">Delete Campaign</h3>
              <p className="text-neutral-300 mb-4">
                Are you sure you want to delete "{deletingCampaign?.name}"? This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={confirmDeleteCampaign}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteCampaignConfirm(false)}
                  className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Contact Confirmation */}
        {showDeleteContactConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-white mb-4">Delete Contact</h3>
              <p className="text-neutral-300 mb-4">
                Are you sure you want to delete "{deletingContact?.name}"? This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={confirmDeleteContact}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteContactConfirm(false)}
                  className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 